import { useState } from 'react';
import type { CabinBannedUser, CabinBannedIP, CabinModHistoryEntry } from '@/mocks/adminSettingsCabina';

interface Props {
  users: CabinBannedUser[];
  ips: CabinBannedIP[];
  history: CabinModHistoryEntry[];
  onSave: () => void;
  saving: boolean;
}

type SubTab = 'usuarios' | 'ips' | 'historial';

const subTabs: { id: SubTab; label: string; icon: string; count: (u: CabinBannedUser[], i: CabinBannedIP[], h: CabinModHistoryEntry[]) => number }[] = [
  { id: 'usuarios', label: 'Usuarios', icon: 'ri-user-forbid-line', count: (u) => u.filter((b) => b.activo).length },
  { id: 'ips', label: 'IPs', icon: 'ri-global-line', count: (_, i) => i.filter((ip) => ip.activo).length },
  { id: 'historial', label: 'Historial', icon: 'ri-history-line', count: (_, __, h) => h.length },
];

export default function CabinBansSection({ users, ips, history, onSave, saving }: Props) {
  const [subTab, setSubTab] = useState<SubTab>('usuarios');

  const activeUsers = users.filter((u) => u.activo);
  const activeIPs = ips.filter((ip) => ip.activo);

  const tipoBadge = (tipo: string) => {
    switch (tipo) {
      case 'permanente': return 'bg-red-100 text-red-700';
      case 'temporal': return 'bg-amber-100 text-amber-700';
      case 'ip': return 'bg-rose-100 text-rose-700';
      default: return 'bg-background-200 text-foreground-500';
    }
  };

  const targetIcon = (t: CabinModHistoryEntry['tipoObjetivo']) => {
    switch (t) {
      case 'usuario': return 'ri-user-line text-rose-500';
      case 'mensaje': return 'ri-chat-3-line text-amber-500';
      case 'ip': return 'ri-global-line text-violet-500';
      case 'palabra': return 'ri-file-text-line text-emerald-500';
      case 'sistema': return 'ri-shield-line text-foreground-500';
    }
  };

  return (
    <div className="space-y-5">
      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-background-200 pb-2">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
              subTab === tab.id
                ? 'bg-red-500 text-background-50'
                : 'text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} text-xs`}></i>
            {tab.label}
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
              subTab === tab.id ? 'bg-background-50/20' : 'bg-background-200'
            }`}>
              {tab.count(users, ips, history)}
            </span>
          </button>
        ))}
      </div>

      {/* Users table */}
      {subTab === 'usuarios' && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Usuario</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden md:table-cell">Motivo</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Tipo</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden sm:table-cell">Duración</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden md:table-cell">Fecha</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Estado</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((u) => (
                <tr key={u.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                        <i className="ri-user-forbid-line text-red-500 text-xs"></i>
                      </div>
                      <span className="font-semibold text-foreground-800 text-xs">{u.username}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-foreground-600 hidden md:table-cell">
                    <span className="line-clamp-1 max-w-[220px]">{u.motivo}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold ${tipoBadge(u.tipo)}`}>
                      {u.tipo === 'permanente' ? 'Permanente' : u.tipo === 'temporal' ? 'Temporal' : 'IP'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-foreground-500 hidden sm:table-cell">{u.duracion}</td>
                  <td className="px-3 py-3 text-foreground-400 hidden md:table-cell">{u.fecha}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-[9px] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                      Activo
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {activeUsers.length === 0 && (
            <div className="text-center py-10 text-foreground-300 text-xs">No hay usuarios baneados activos.</div>
          )}
        </div>
      )}

      {/* IPs table */}
      {subTab === 'ips' && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">IP</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden md:table-cell">Motivo</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden sm:table-cell">Fecha</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden md:table-cell">Baneado por</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Estado</th>
              </tr>
            </thead>
            <tbody>
              {activeIPs.map((ip) => (
                <tr key={ip.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center">
                        <i className="ri-global-line text-rose-500 text-xs"></i>
                      </div>
                      <code className="font-mono text-xs font-semibold text-foreground-800">{ip.ip}</code>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-foreground-600 hidden md:table-cell">
                    <span className="line-clamp-1 max-w-[200px]">{ip.motivo}</span>
                  </td>
                  <td className="px-3 py-3 text-foreground-400 hidden sm:table-cell">{ip.fecha}</td>
                  <td className="px-3 py-3 text-foreground-500 hidden md:table-cell">{ip.baneadoPor}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-600 text-[9px] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                      Bloqueada
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {activeIPs.length === 0 && (
            <div className="text-center py-10 text-foreground-300 text-xs">No hay IPs bloqueadas activas.</div>
          )}
        </div>
      )}

      {/* History table */}
      {subTab === 'historial' && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Fecha</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Admin</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px]">Acción</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden md:table-cell">Objetivo</th>
                <th className="text-left px-3 py-2.5 text-foreground-400 font-medium text-[10px] hidden lg:table-cell">Detalles</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                  <td className="px-3 py-3">
                    <span className="text-foreground-400 text-[10px] font-mono">{entry.timestamp}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      <i className={`${entry.admin === 'Sistema' ? 'ri-robot-line text-foreground-400' : 'ri-user-line text-foreground-500'} text-xs`}></i>
                      <span className="text-foreground-700 text-xs font-medium">{entry.admin}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-foreground-800 text-xs font-semibold">{entry.accion}</span>
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell">
                    <div className="flex items-center gap-1.5">
                      <i className={`${targetIcon(entry.tipoObjetivo)} text-xs`}></i>
                      <span className="text-foreground-600 text-xs">{entry.objetivo}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell">
                    <span className="text-foreground-500 text-[10px] line-clamp-1 max-w-[280px]">{entry.detalles}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Save */}
      <div className="flex items-center justify-between pt-2 border-t border-background-200">
        <p className="text-foreground-400 text-[10px]">
          {activeUsers.length} usuarios · {activeIPs.length} IPs · {history.length} registros
        </p>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-check-line text-sm"></i>
              Guardar Cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}